/**
 * The signed-out homepage, set as a broadsheet front page: the masthead, the
 * lead story (talking to the log through an AI assistant), then the log, the
 * shared household, the overview, and the price. Screenshots are real screens
 * from a seeded demo household (see docs/ENGINEERING.md, "Landing page").
 * Signing in from any button lands in the app.
 */
import { Link } from "@tanstack/react-router";
import { SignInButtons } from "../components/sign-in.tsx";
import { Masthead, Section } from "../components/landing/broadsheet.tsx";
import { ConnectSteps } from "../components/landing/connect.tsx";
import { Conversation } from "../components/landing/conversation.tsx";
import { LegalLinks } from "../components/legal-links.tsx";
import { cn } from "../lib/utils.ts";
import logShot from "../screenshots/log.webp";
import nightShot from "../screenshots/night.webp";
import overviewShot from "../screenshots/overview.webp";
import sleepShot from "../screenshots/sleep-window.webp";

/** Phone screenshots are 600 × 1301 (a 393pt-wide phone); the iPad one 1600 × 1112. */
const PHONE = { width: 600, height: 1301 };
const TABLET = { width: 1600, height: 1112 };

/** A screenshot with its caption, captioned like a newspaper photo. */
function Shot({
  src,
  alt,
  caption,
  size = PHONE,
  className,
}: {
  src: string;
  alt: string;
  caption: React.ReactNode;
  size?: { width: number; height: number };
  className?: string;
}) {
  return (
    <figure className={cn("min-w-0", className)}>
      <img
        src={src}
        alt={alt}
        width={size.width}
        height={size.height}
        loading="lazy"
        decoding="async"
        className="h-auto w-full rounded-lg border border-divider"
      />
      <figcaption className="mt-2 text-[13px] leading-snug opacity-70">{caption}</figcaption>
    </figure>
  );
}

/** A short titled paragraph: one feature. */
function Point({
  emoji,
  title,
  children,
}: {
  emoji?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="border-t border-rule py-3">
      <h3 className="heading text-[18px]">
        {emoji ? (
          <span aria-hidden="true" className="mr-2">
            {emoji}
          </span>
        ) : null}
        {title}
      </h3>
      <p className="mt-0.5 text-[15px] opacity-80">{children}</p>
    </li>
  );
}

function Question({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="group border-t border-rule">
      <summary className="heading flex min-h-[52px] cursor-pointer list-none items-center gap-3 py-2 text-[18px] [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">{q}</span>
        <span
          aria-hidden="true"
          className="text-[22px] font-normal leading-none text-accent group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <div className="pb-4 text-[15px] opacity-85">{children}</div>
    </details>
  );
}

export function Landing() {
  return (
    // No household data here, so session replays may show its words.
    <div
      data-ph-unmask
      className="mx-auto min-h-dvh w-full max-w-[1120px] pb-12 pl-[max(20px,env(safe-area-inset-left))] pr-[max(20px,env(safe-area-inset-right))] md:pl-[max(32px,env(safe-area-inset-left))] md:pr-[max(32px,env(safe-area-inset-right))]"
      style={{ paddingTop: "calc(16px + env(safe-area-inset-top))" }}
    >
      <title>Babia — the twin log you can talk to</title>
      <Masthead />

      <main>
        {/* The lead story. */}
        <section
          aria-labelledby="lead"
          className="mt-8 grid items-center gap-x-12 gap-y-10 md:mt-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]"
        >
          <div className="min-w-0">
            <p className="kicker !opacity-75">Designed and tested for twins and multiples</p>
            <h1
              id="lead"
              className="heading mt-3 text-[44px] leading-[1.02] tracking-[-0.035em] md:text-[64px]"
            >
              The twin log <br className="md:hidden lg:inline" />
              you can talk to.
            </h1>
            <p className="mt-5 text-[18px] leading-snug md:text-[20px]">
              Babia is built for two babies from the first tap. Tell ChatGPT, Claude, or Gemini
              “both had 4 oz at 2am” and it’s on the log you share with your partner. Ask when Bea
              last ate, how the twins slept this week, or whether anything was forgotten. Less
              guessing, more sleep.
            </p>
            <p className="mt-3 text-[15px] opacity-70">
              Bottles, changes, naps, meds, and notes for every baby. Two taps in the app, or one
              sentence to your assistant.
            </p>
            <SignInButtons className="mt-7 max-w-[400px]" showReturned />
            <p className="mt-3 text-[14px]">
              <span className="heading text-accent-2">Free for 14 days</span>
              <span className="opacity-70">
                {" "}
                — every feature. Then $1.99 once for the whole family, the founding price for the
                first 100 families.
              </span>
            </p>
          </div>
          <Conversation className="mx-auto w-full max-w-[520px]" />
        </section>

        <Section id="twins" title="Made for twins" note="And triplets, and more">
          <p className="mt-4 max-w-[760px] text-[17px]">
            Babia isn’t a one-baby app with a second profile added on. It starts from two: setup
            begins with a row for each twin, and every screen is designed and tested with twins.
          </p>
          <ul className="mt-2 grid gap-x-10 md:grid-cols-2 lg:grid-cols-3">
            <Point emoji="👯" title="Both babies, one glance">
              A tab for each baby, one tap apart. A sleeping twin’s tab shows 😴, so you know who’s
              down without switching.
            </Point>
            <Point emoji="🍼" title="One tap for both">
              “Save for both” in the bottle sheet logs the same bottle for each twin. Or tell your
              assistant “both had 3 oz”, using their names, their nicknames, or just “both”.
            </Point>
            <Point emoji="🔎" title="One twin, not the other">
              When your twins usually eat and change together, “Needs a look” notices a bottle or
              change logged for one and not the other.
            </Point>
            <Point emoji="📊" title="Side by side">
              On a tablet, the overview puts twins next to each other: sleep windows, bottles, and
              changes on the same scales, then each baby’s notes and milestones.
            </Point>
            <Point emoji="🏷️" title="Names, not “the baby”">
              Your assistant knows each twin by name and nickname, and asks “which baby?” rather
              than guessing.
            </Point>
            <Point emoji="👨‍👩‍👧‍👦" title="Triplets and more">
              Up to 10 babies in one household, each with their own tab, timeline, and trends.
            </Point>
          </ul>
        </Section>

        <Section
          id="assistant"
          title="Just say what happened"
          note="ChatGPT, Claude, Gemini, or any assistant that speaks MCP"
        >
          <ul className="mt-4 grid gap-x-10 md:grid-cols-2">
            <Point emoji="🗣️" title="Log it out loud">
              Bottles, changes, naps, meds, and notes, for one baby or both at once: “Bea’s asleep”,
              “wet and dirty for Ada”, “both had 3 oz twenty minutes ago”. Your usual formula and
              medicine names are matched, and a near miss gets a “did you mean?”, not a new name.
            </Point>
            <Point emoji="💬" title="Ask about the data">
              “When did Bea last eat?” “How long did Ada sleep last night?” “How many ounces so far
              today?” Your assistant reads the log and answers in your household’s time zone.
            </Point>
            <Point emoji="🔎" title="Catch what was forgotten">
              “Did we miss anything?” runs the overview’s checks: a nap left running, a bottle or
              change overdue for that baby, one twin logged without the other, a daily medicine not
              given yet. It offers the fix; nothing changes until you say so.
            </Point>
            <Point emoji="✏️" title="Fix it in a sentence">
              “That bottle was Bea’s, not Ada’s.” “Make it 3.5 oz.” “She woke up at 4:10.” Move,
              edit, or delete any entry by asking.
            </Point>
          </ul>

          <h3 className="heading mt-10 text-[22px]">Connect in a minute</h3>
          <ConnectSteps />
          <p className="mt-6 max-w-[760px] text-[14px] opacity-70">
            Babia never calls an AI model itself and holds no AI keys: your own assistant
            understands you and uses Babia’s tools to read and write your log. It can log, read,
            edit, and delete entries — not change your babies, settings, or partner.
          </p>
        </Section>

        <Section id="log" title="Two taps at 3am" note="The log, on any phone">
          <div className="mt-6 grid gap-x-12 gap-y-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              <Shot
                src={logShot}
                alt="Babia's log for Ada: last feed 1 hr 10 min ago, 10 oz today, a large Log a bottle button, tiles for Change, Woke up, Meds, and Note, and today's timeline."
                caption="10:24 am. Ada has been asleep 36 minutes; her tab and the sleep tile say so."
              />
              <Shot
                src={nightShot}
                alt="Babia's log for Bea in night mode at 3 am: last feed 12 min ago, 3.5 oz today, with the bottle and change just logged."
                caption="3:12 am, night mode. Bea’s bottle and change, logged one-handed."
              />
            </div>
            <ul className="min-w-0">
              <Point emoji="🍼" title="Bottles">
                In ounces or millilitres, with your preset amounts and the usual bottle one tap
                away. With twins, “Save for both” logs it for each baby at once.
              </Point>
              <Point emoji="🧷" title="Changes">
                Wet, dirty, or both — and how long since the last one.
              </Point>
              <Point emoji="😴" title="Sleep">
                One toggle: “Fell asleep”, then “Woke up”. A sleeping baby’s tab shows 😴, even
                across midnight.
              </Point>
              <Point emoji="💊" title="Meds and notes">
                Your own short list of medicines, each a single tap for its usual dose, and notes
                for baths, weights, poops, sleep, and anything else.
              </Point>
              <Point title="Last feed, at a glance">
                “12 min ago” in big type, today’s ounces and changes beside it. Tap × on a row to
                remove it; Undo brings it back.
              </Point>
              <Point title="Night mode">
                Dark paper for the small hours, without a white flash when you open it.
              </Point>
            </ul>
          </div>
        </Section>

        <Section id="shared" title="One log, two parents" note="Shared within seconds">
          <ul className="mt-4 grid gap-x-10 md:grid-cols-3">
            <Point emoji="👥" title="Each on your own phone">
              Sign in with Google or Facebook. Your partner joins with a six-character code, and you
              both log to the same page.
            </Point>
            <Point emoji="🔄" title="Always in step">
              A tap on one phone shows on the other within seconds, whoever logged it — app or
              assistant.
            </Point>
            <Point emoji="🌙" title="Hand it over">
              A night nurse or grandparent can log on a phone that’s already signed in. Nothing to
              set up.
            </Point>
          </ul>
        </Section>

        <Section id="overview" title="The overview" note="For the moments between feeds">
          <div className="mt-6 grid gap-x-12 gap-y-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <Shot
              src={overviewShot}
              alt="Babia's overview on a phone: Needs a look flags that Bea's nap from 7:07 am is still running, then new milestones and a Right now box score for each twin."
              caption="“Needs a look” spots Bea’s nap still running after her change was logged — a forgotten Wake up tap."
              className="mx-auto w-full max-w-[300px] md:mx-0"
            />
            <ul className="min-w-0">
              <Point emoji="🔎" title="Needs a look">
                A nap left running, a bottle or change overdue against that baby’s own rhythm, one
                twin logged without the other, a daily medicine not yet given. It asks whether you
                forgot to tap — never whether your baby is eating or sleeping enough.
              </Point>
              <Point emoji="🎉" title="Milestones">
                Every first as it happens: the first 6-hour stretch, the twins’ first 4-hour sleep
                together, the 100th bottle.
              </Point>
              <Point emoji="📊" title="Trends by day, week, and month">
                Two weeks of sleep windows from noon to noon, bottles and changes a day, a year of
                each baby’s weeks and months, and a “right now” box score for every baby.
              </Point>
              <Point emoji="📝" title="Your notes, at a glance">
                A word cloud of each baby’s notes. Tap “gassy” to read every note that mentions it.
              </Point>
            </ul>
          </div>
          <Shot
            src={sleepShot}
            size={TABLET}
            alt="Babia's overview on an iPad: two weeks of sleep windows for Ada and Bea side by side, each night's longest stretch in solid yellow, above daily bottle charts."
            caption="On an iPad, twins side by side. Ada’s longest stretch has grown from 3 hr 5 min to 6 hr in two weeks."
            className="mt-10"
          />
        </Section>

        <Section id="everywhere" title="Wherever you are" note="Phone, tablet, home screen">
          <ul className="mt-4 grid gap-x-10 md:grid-cols-3">
            <Point emoji="📱" title="Phones and iPads">
              Fills a phone screen; on a tablet, the actions stay pinned beside the timeline.
            </Point>
            <Point emoji="🏠" title="On your home screen">
              Add Babia to your home screen and it opens full screen, like an app.
            </Point>
            <Point emoji="👶" title="One baby works too">
              Everything works the same with a single baby, and nothing asks which one.
            </Point>
          </ul>
        </Section>

        <section
          aria-labelledby="free"
          className="mt-16 border-y-[3px] border-ink py-8 md:mt-20 md:py-10"
        >
          <div className="grid items-center gap-x-12 gap-y-6 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            <div className="min-w-0">
              <h2
                id="free"
                className="heading text-[34px] leading-[1.05] tracking-[-0.03em] md:text-[44px]"
              >
                Two weeks free. Then $1.99, once.
              </h2>
              <p className="mt-3 text-[17px]">
                Try every feature on this page free for 14 days: the shared log, the overview, and
                the AI assistant connection. No card to start. Then anyone in the family buys Babia
                once for everyone, up to 10 carers: $1.99 for the first 100 founding families, $4.99
                after that. No subscription, no ads.
              </p>
            </div>
            <SignInButtons className="w-full md:max-w-[400px] md:justify-self-end" />
          </div>
        </section>

        <Section id="questions" title="Questions">
          <div className="mt-2 max-w-[760px]">
            <Question q="Why a baby log made for twins?">
              Because with twins, the question at 3am isn’t only “when did the baby eat?” but “which
              baby ate?” Babia keeps every baby’s log one tap apart, shows them side by side, and
              notices when one twin was logged and the other wasn’t. It works for one baby too, and
              for up to 10.
            </Question>
            <Question q="What happens after the free trial?">
              Your family’s 14 days start when you add your babies. When they end, the log stays
              readable, with every entry kept, but adding entries waits until someone in the family
              buys Babia. One purchase covers the whole household, up to 10 carers, and never
              renews. Payment is handled by Polar, so Babia never sees your card.
            </Question>
            <Question q="Do I need an AI assistant?">
              No. The app does everything on its own. The assistant is another way in, for when
              talking is easier than tapping — or when you want to ask the log a question.
            </Question>
            <Question q="Which assistants work?">
              Any assistant that can add a custom connector (a remote MCP server) by URL — ChatGPT,
              Claude, Gemini, Grok, and Perplexity among them. Claude works on its free plan;
              ChatGPT and Perplexity take a paid plan. In ChatGPT, set it up on the web: turn on
              Developer mode under Settings → Security and login, then add Babia as an app. Gemini’s
              custom apps are for personal accounts in the US. Microsoft Copilot and Meta AI can’t
              add connectors yet. Once you’re signed in, the ✨ in the app walks you through yours.
              Each partner can connect their own.
            </Question>
            <Question q="What can a connected assistant do?">
              Log bottles, changes, naps, meds, and notes; read your household’s log; edit, move, or
              delete entries; and tell you what looks forgotten. You allow it on a consent screen
              first. It can’t change your babies, settings, or partner. You can see the assistants
              you allowed in Settings, and remove one there at any time.
            </Question>
            <Question q="Is my log private?">
              Your log is shared with the people in your household, and with an AI assistant only if
              you connect one and allow it. Babia itself never sends your log to an AI model,
              doesn’t sell your information, and shows no ads. Usage analytics record how the app is
              used (which kinds of entries are added, for example) but never your babies’ names,
              amounts, medicines, or notes, and you can turn them off for your browser in Settings.
              Shiny Pebble Tech LLC, which runs Babia, may access it for support, maintenance, and
              security. The Privacy Policy below has the details, including the providers that host
              the service.
            </Question>
            <Question q="How does Babia compare with other trackers?">
              We put Babia next to Huckleberry, Baby Tracker, Baby Connect, and Nara Baby, feature
              by feature: what Babia has, what it leaves out, and what is coming soon.{" "}
              <Link to="/compare" className="underline underline-offset-4">
                See the comparison
              </Link>
              .
            </Question>
            <Question q="Is Babia medical advice?">
              No. Babia is a care log for what happened and when. It shows where a logged weight
              falls on the WHO growth curves, as your pediatrician’s chart does, but it never flags
              or judges a percentile. Always follow your clinician’s advice.
            </Question>
          </div>
        </Section>
      </main>

      <footer className="mt-16 border-t border-divider pt-4">
        <p className="mb-2 text-[13px] opacity-65">
          By continuing, you agree to the Terms of Service. Read our Privacy Policy to learn how
          Babia handles your information.
        </p>
        <Link
          to="/compare"
          className="inline-flex min-h-11 items-center text-[14px] underline underline-offset-4"
        >
          How Babia compares
        </Link>
        <LegalLinks />
      </footer>
    </div>
  );
}
